// @flow

/* eslint-disable flowtype/require-exact-type */

import type { Reducer } from 'redux';
import type { ExtractReturn } from '@ta/common.types';
import type { Action as AllActions } from '@ta/common.state';

export const PRIVATE_ACTION_PREFIX = 'PRIVATE_';

export type State = { [string]: any };

const makeGlobalAction = (target: string, privateAction: { type: string, [string]: any }) => ({
  type: `${PRIVATE_ACTION_PREFIX}${privateAction.type}`,
  target,
  privateAction,
});

export type Action = ExtractReturn<typeof makeGlobalAction>;

const privateReducers: { [string]: any } = {};

export const REDUCER: Reducer<State, AllActions> = (state: State = {}, action: any): State => {
  if (typeof action !== 'object') {
    return state;
  }
  const key = action.target;
  if (
    key &&
    typeof action.privateAction === 'object' &&
    action.privateAction.type &&
    action.type === `${PRIVATE_ACTION_PREFIX}${action.privateAction.type}` &&
    privateReducers[key]
  ) {
    const oldSubState = state && state[key];
    const newSubState = privateReducers[key](oldSubState, action.privateAction);
    if (oldSubState !== newSubState) {
      const newState = state ? { ...state } : {};
      newState[key] = newSubState;
      return newState;
    }
  }
  return state;
};

/**
 * Any package can create an instance of this to get its own private slice of the redux state
 *
 * After this is created, call setReducer() to define how the state responds to private actions
 *
 *    TSTATE - type of the private state object
 */
export class PrivateStateAccessor<TSTATE> {
  STATE_KEY: string;
  defaultState: TSTATE;

  /** Allocate a slice of the redux store with a unique key and default value */

  constructor(key: string, defaultState: TSTATE) {
    let hash = 0;
    for (let i = 0; i < key.length; ++i) {
      /* eslint-disable-next-line no-bitwise */
      hash = (hash * 31 + key.charCodeAt(i)) & 65535;
    }
    this.STATE_KEY = `${key}_${hash.toString(16)}`;
    this.defaultState = defaultState;
  }

  /** Set the reducer that defines the state's response to actions -- can only be called once */

  setReducer(reducer: (state: any, action: any) => any) {
    if (privateReducers[this.STATE_KEY]) {
      throw new Error(`private reducer already set for ${this.STATE_KEY}`);
    }
    privateReducers[this.STATE_KEY] = (state, action) => {
      const origState = state || this.defaultState;
      const newState = reducer(origState, action);
      return newState === origState ? state : newState;
    };
  }

  /** Set the reducer if it's not already set (allows multiple instances of the defining module) */

  setReducerIfUnset(reducer: (state: any, action: any) => any) {
    if (privateReducers[this.STATE_KEY]) {
      return false;
    }
    privateReducers[this.STATE_KEY] = (state, action) => {
      const origState = state || this.defaultState;
      const newState = reducer(origState, action);
      return newState === origState ? state : newState;
    };
    return true;
  }

  /** Get your private slice of state from an instance of the global state */

  getFrom(globalState: any): TSTATE {
    return (
      (globalState && globalState.private && globalState.private[this.STATE_KEY]) ||
      this.defaultState
    );
  }

  /** Convert an action on your local state (that your reducer could understand) into
      an action that can be dispatched to global state.  It will be converted back and
      directed to your reducer when it is processed */

  action(subAction: { type: string, [string]: any }): Action {
    return makeGlobalAction(this.STATE_KEY, subAction);
  }
}
